/**
 * Step 2 — Hiring requirements (PRD §7.3): role summary (≥20 meaningful
 * characters) plus ordered lists for responsibilities, required
 * qualifications, nice-to-have, and benefits.
 */
import { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text, type TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { Briefcase } from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { NativeForm } from '../../common/NativeForm';
import { OrderedListEditor } from './OrderedListEditor';
import { FlowTextField } from './post-job-ui';
import type { PostJobFlowController } from './use-post-job-flow';

export function PostJobRequirementsStep({
  flow,
}: {
  flow: PostJobFlowController;
}) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  const summaryRef = useRef<TextInput>(null);

  const { state, setContent, errorFor, focusFieldId, clearFocusField } = flow;

  useEffect(() => {
    if (focusFieldId === 'summary') {
      summaryRef.current?.focus();
      clearFocusField();
    }
  }, [clearFocusField, focusFieldId]);

  return (
    <NativeForm style={styles.wrap}>
      {state.entry.mode === 'ai' ? (
        <View style={[styles.aiBadge, { backgroundColor: theme.buttonSoftBg }]}>
          <Briefcase size={12} color={colors.primary} strokeWidth={2} />
          <Text style={[styles.aiBadgeText, { color: colors.primary }]}>
            {t('jobFlow.aiBadge')}
          </Text>
        </View>
      ) : null}

      <FlowTextField
        ref={summaryRef}
        label={t('jobFlow.fields.summary') as string}
        value={state.content.summary}
        onChangeText={(summary) => setContent({ summary })}
        placeholder={t('jobFlow.placeholders.summary') as string}
        errorKey={errorFor('summary')}
        multiline
        minHeight={140}
      />

      <OrderedListEditor
        label={t('jobFlow.fields.responsibilities') as string}
        items={state.content.responsibilities}
        onChange={(responsibilities) => setContent({ responsibilities })}
        placeholder={t('jobFlow.placeholders.responsibility') as string}
        errorKey={errorFor('responsibilities')}
      />

      <OrderedListEditor
        label={t('jobFlow.fields.requirements') as string}
        items={state.content.requirements}
        onChange={(requirements) => setContent({ requirements })}
        placeholder={t('jobFlow.placeholders.requirement') as string}
        errorKey={errorFor('requirements')}
      />

      <OrderedListEditor
        label={t('jobFlow.fields.niceToHave') as string}
        items={state.content.niceToHave}
        onChange={(niceToHave) => setContent({ niceToHave })}
        placeholder={t('jobFlow.placeholders.niceToHave') as string}
      />

      <OrderedListEditor
        label={t('jobFlow.fields.benefits') as string}
        items={state.content.benefits}
        onChange={(benefits) => setContent({ benefits })}
        placeholder={t('jobFlow.placeholders.benefit') as string}
      />
    </NativeForm>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[6],
  },
  aiBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  aiBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
});
